import React, { useCallback, useEffect, useState } from "react";
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Typography,
    CircularProgress,
    Alert,
    Chip,
    Grid,
    Card,
    CardContent,
    MenuItem,
    TextField,
    IconButton,
    Tooltip,
} from "@mui/material";
import { Refresh as RefreshIcon } from "@mui/icons-material";
import { getServiceStatus, listUnhealthyServices } from "../../api/superAdminApi";
import { WB_SX, WB_UI } from "../../ui/ux/uiTokens";
import { SERVICE_LABELS } from "./adminUtils";

const SummaryCard = ({ title, data, accent }) => (
    <Card elevation={0} sx={{ ...WB_SX.card, bgcolor: WB_UI.color.cardBgStrong, borderLeft: `4px solid ${accent}`, height: "100%" }}>
        <CardContent>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>{title}</Typography>
            <Grid container spacing={1}>
                <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Total</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>{data?.total ?? 0}</Typography>
                </Grid>
                <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Healthy</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: "#047857" }}>{data?.healthy ?? 0}</Typography>
                </Grid>
                <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Unhealthy</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: "#b91c1c" }}>{data?.unhealthy ?? 0}</Typography>
                </Grid>
                <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Stale</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>{data?.stale ?? 0}</Typography>
                </Grid>
            </Grid>
        </CardContent>
    </Card>
);

const AdminServiceHealth = () => {
    const [overview, setOverview] = useState(null);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [serviceFilter, setServiceFilter] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(15);

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const params = { page: page + 1, per_page: rowsPerPage };
            if (serviceFilter) params.service = serviceFilter;

            const [status, unhealthy] = await Promise.all([
                getServiceStatus(),
                listUnhealthyServices(params),
            ]);
            setOverview(status);
            setRows(unhealthy.rows);
            setTotal(unhealthy.meta.total ?? unhealthy.rows.length);
        } catch (err) {
            setError(err.message || "Failed to load service health");
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, serviceFilter]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { setPage(0); }, [serviceFilter]);

    return (
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 0.5 }}>
                <Typography variant="h5" sx={{ ...WB_SX.pageTitle, borderLeft: "4px solid #dc2626", pl: 1.5 }}>
                    Service health
                </Typography>
                <Tooltip title="Refresh">
                    <IconButton onClick={load} disabled={loading}><RefreshIcon /></IconButton>
                </Tooltip>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 720, lineHeight: 1.65 }}>
                Monitor script heartbeats. A process is healthy when its last heartbeat is within 5 minutes and status flags are active.
            </Typography>

            {error ? <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert> : null}

            {overview && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={4}>
                        <SummaryCard title="Email" data={overview.email} accent="#6d28d9" />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <SummaryCard title="WhatsApp Business" data={overview.whatsapp_business} accent="#047857" />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <SummaryCard title="Personal WhatsApp" data={overview.personal_whatsapp} accent="#1d4ed8" />
                    </Grid>
                </Grid>
            )}

            <Box sx={{ mb: 2 }}>
                <TextField select size="small" label="Filter by service" value={serviceFilter} onChange={(e) => setServiceFilter(e.target.value)} sx={{ minWidth: 220 }}>
                    <MenuItem value="">All unhealthy</MenuItem>
                    <MenuItem value="email">Email</MenuItem>
                    <MenuItem value="whatsapp_business">WhatsApp Business</MenuItem>
                    <MenuItem value="personal_whatsapp">Personal WhatsApp</MenuItem>
                </TextField>
            </Box>

            <TableContainer component={Paper} elevation={0} sx={{ ...WB_SX.card, bgcolor: WB_UI.color.cardBgStrong, overflow: "auto" }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {["User", "Service", "Status", "Heartbeat age", "Health"].map((h) => (
                                <TableCell key={h} sx={WB_SX.tableHeadCell}>{h}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                                    <CircularProgress size={28} />
                                </TableCell>
                            </TableRow>
                        ) : rows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                                    <Typography variant="body2" color="text.secondary">No unhealthy services found.</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            rows.map((row, idx) => (
                                <TableRow key={idx} sx={WB_SX.tableRow}>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {row.user_name || row.user_id || "—"}
                                        </Typography>
                                        {row.user_email && (
                                            <Typography variant="caption" color="text.secondary">{row.user_email}</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>{SERVICE_LABELS[row.service] || row.service}</TableCell>
                                    <TableCell>
                                        <Typography variant="caption">{row.status || row.process_status || row.script_status || "—"}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        {row.heartbeat_age_minutes != null ? `${row.heartbeat_age_minutes} min` : "—"}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={row.is_healthy ? "Healthy" : "Unhealthy"}
                                            sx={{
                                                fontWeight: 600,
                                                bgcolor: row.is_healthy ? "rgba(16,185,129,0.12)" : "rgba(239,68,68,0.12)",
                                                color: row.is_healthy ? "#047857" : "#b91c1c",
                                            }}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[10, 15, 25, 50]}
            />
        </Box>
    );
};

export default AdminServiceHealth;
